import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, consulta } from '../api/cliente';
import type { Pagina } from '../api/cliente';
import type { RegistroAuditoria, Usuario } from '../api/tipos';
import {
  Aviso,
  Campo,
  Cargando,
  Encabezado,
  Paginador,
  Seleccion,
  Tarjeta,
  Vacio,
  fechaHora,
} from '../ui/piezas';

const ENTIDADES = [
  'inmueble',
  'propietario',
  'mandato',
  'liquidacion',
  'proyecto',
  'lead',
  'cita',
  'aliado',
  'caracteristica',
  'usuario',
  'archivo',
];

/** Solo lo que cambió: repetir los treinta campos iguales esconde el que se movió. */
function diferencias(
  antes: Record<string, unknown> | null,
  despues: Record<string, unknown> | null,
): { campo: string; antes: string; despues: string }[] {
  const claves = new Set([...Object.keys(antes ?? {}), ...Object.keys(despues ?? {})]);
  const filas: { campo: string; antes: string; despues: string }[] = [];
  for (const clave of claves) {
    const a = antes?.[clave];
    const d = despues?.[clave];
    if (JSON.stringify(a) === JSON.stringify(d)) continue;
    filas.push({
      campo: clave,
      antes: a === undefined || a === null ? '—' : String(a),
      despues: d === undefined || d === null ? '—' : String(d),
    });
  }
  return filas;
}

export default function Bitacora() {
  const [entidad, setEntidad] = useState('');
  const [usuario, setUsuario] = useState('');
  const [desde, setDesde] = useState('');
  const [pagina, setPagina] = useState(0);

  const usuarios = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api<Usuario[]>('/admin/usuarios'),
  });

  const listado = useQuery({
    queryKey: ['auditoria', entidad, usuario, desde, pagina],
    queryFn: () =>
      api<Pagina<RegistroAuditoria>>(
        `/admin/auditoria${consulta({
          entidad,
          usuario,
          // El input da una fecha; la API espera un instante.
          desde: desde ? `${desde}T00:00:00Z` : '',
          page: pagina,
          size: 50,
        })}`,
      ),
  });

  const filas = listado.data?.content ?? [];

  return (
    <>
      <Encabezado
        titulo="Bitácora"
        nota="Quién cambió qué, y qué había antes. Toda lectura de datos bancarios queda aquí."
      />

      <Aviso error={listado.error} />

      <Tarjeta>
        <div className="rejilla" style={{ marginBottom: 16 }}>
          <Seleccion
            etiqueta="Sobre"
            valor={entidad}
            alCambiar={(v) => {
              setEntidad(v);
              setPagina(0);
            }}
            vacio="Todo"
            opciones={ENTIDADES.map((e) => ({ valor: e, texto: e }))}
          />
          <Seleccion
            etiqueta="Quién"
            valor={usuario}
            alCambiar={(v) => {
              setUsuario(v);
              setPagina(0);
            }}
            vacio="Cualquiera"
            opciones={(usuarios.data ?? []).map((u) => ({
              valor: u.id,
              texto: `${u.nombre} ${u.apellido}`,
            }))}
          />
          <Campo
            etiqueta="Desde"
            tipo="date"
            valor={desde}
            alCambiar={(v) => {
              setDesde(v);
              setPagina(0);
            }}
          />
        </div>

        {listado.isPending ? (
          <Cargando />
        ) : filas.length === 0 ? (
          <Vacio>Nada registrado con esos filtros.</Vacio>
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Quién</th>
                  <th>Qué hizo</th>
                  <th>Cambios</th>
                  <th>Desde</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((r) => {
                  const cambios = diferencias(r.antes, r.despues);
                  return (
                    <tr key={r.id}>
                      <td>{fechaHora(r.ocurrioEn)}</td>
                      <td>{r.usuario ?? 'sistema'}</td>
                      <td>
                        <div>{r.accion.replaceAll('_', ' ')}</div>
                        <small style={{ opacity: 0.7 }}>{r.entidad}</small>
                      </td>
                      <td>
                        {cambios.length === 0 ? (
                          <span style={{ opacity: 0.6 }}>—</span>
                        ) : (
                          cambios.map((c) => (
                            <div key={c.campo}>
                              <small>
                                <strong>{c.campo}</strong>: {c.antes} → {c.despues}
                              </small>
                            </div>
                          ))
                        )}
                      </td>
                      <td>
                        <small>{r.ip ?? '—'}</small>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Paginador
          pagina={listado.data?.number ?? 0}
          total={listado.data?.totalElements ?? 0}
          paginas={listado.data?.totalPages ?? 0}
          alCambiar={setPagina}
        />
      </Tarjeta>
    </>
  );
}
